import React from "react";
import PropTypes from "prop-types";
import DisclaimerText from "components/ai_tools/shared/AIForm/DisclaimerText";
import { useAccessTier } from "contexts/AccessTierContext";
import {
  AI_TOOL_NAMES,
  AMPLITUDE_SIGNUP_INTRO_CHAT_ATTRIBUTES
} from "../../constants";
import useWindowResizeAndFormCollapse from "../../hooks/useWindowResizeAndFormCollapse";
import FreeTierModalLink from "../AIForm/FreeTierModalLink";

const InitialMessageBullets = ({
  inContent,
  locked,
  quotaRemaining,
  signupPath,
  toolName,
  upgradeable
}) => {
  const { isLessThanSmallBreakpoint } = useWindowResizeAndFormCollapse();
  const { isSignedOut, isFree, isAPlusTeacher } = useAccessTier();
  const amplitudeKey = AMPLITUDE_SIGNUP_INTRO_CHAT_ATTRIBUTES[toolName];

  switch (toolName) {
    case AI_TOOL_NAMES.ASK_AI:
      if (isSignedOut) {
        return (
          <FreeTierModalLink
            amplitudeKey={
              inContent
                ? "aiContentToolFreeTierSignUp"
                : "standaloneFreeTierSignUpChatBubble"
            }
            buttonText="Try it for free!"
          />
        );
      }

      if (isFree && !inContent) {
        return (
          <ol className="body-2 ask-litcharts-ai--list">
            <li>Select any title in the dropdown</li>
            <li>Enter and submit your question</li>
          </ol>
        );
      }
      if (inContent) {
        return (
          <div className="ai-form--disclaimer">
            <DisclaimerText />
          </div>
        );
      }

      return (
        <ol className="body-2 ask-litcharts-ai--list">
          {locked && (
            <li>
              <a href={signupPath} data-amplitude={amplitudeKey}>
                Sign up for LitCharts A<sup>+</sup>
              </a>
            </li>
          )}
          {!locked && quotaRemaining > 0 && (
            <li className="ask-litcharts-ai--list_ask-step">
              Tap the Ask button
            </li>
          )}
          <li>Select a title in the dropdown</li>
          <li>Submit your question about that title</li>
        </ol>
      );
    case AI_TOOL_NAMES.DISCUSSION_QUESTIONS:
      if (inContent && isAPlusTeacher)
        return (
          <div className="ai-form--disclaimer">
            <DisclaimerText />
          </div>
        );
      if (inContent) return null;
      return (
        <ol className="body-2 ask-litcharts-ai--list">
          {locked && upgradeable && (
            <li>
              <a href={signupPath} data-amplitude={amplitudeKey}>
                Upgrade to LitCharts A<sup>+</sup> Teacher
              </a>
            </li>
          )}
          {locked && !upgradeable && (
            <li>
              <a href={signupPath} data-amplitude={amplitudeKey}>
                Sign up for LitCharts A<sup>+</sup> Teacher
              </a>
            </li>
          )}
          {!locked && isLessThanSmallBreakpoint && (
            <li>Tap the Generate Questions button</li>
          )}
          <li>Submit a title and discussion topic</li>
          <li>Get your discussion questions</li>
        </ol>
      );
    case AI_TOOL_NAMES.QUIZ_QUESTIONS:
      return (
        <ol className="body-2 ask-litcharts-ai--list">
          {locked && upgradeable && (
            <li>
              <a href={signupPath} data-amplitude={amplitudeKey}>
                Upgrade to LitCharts A<sup>+</sup> Teacher
              </a>
            </li>
          )}
          {locked && !upgradeable && (
            <li>
              <a href={signupPath} data-amplitude={amplitudeKey}>
                Sign up for LitCharts A<sup>+</sup> Teacher
              </a>
            </li>
          )}
          {!locked && isLessThanSmallBreakpoint && (
            <li>Tap the Generate Questions button</li>
          )}
          <li>Submit a title and quiz topic</li>
          <li>Get your quiz questions</li>
        </ol>
      );
    case AI_TOOL_NAMES.ESSAY_PROMPTS:
      return (
        <ol className="body-2 ask-litcharts-ai--list">
          {locked && upgradeable && (
            <li>
              <a href={signupPath} data-amplitude={amplitudeKey}>
                Upgrade to LitCharts A<sup>+</sup> Teacher
              </a>
            </li>
          )}
          {locked && !upgradeable && (
            <li>
              <a href={signupPath} data-amplitude={amplitudeKey}>
                Sign up for LitCharts A<sup>+</sup> Teacher
              </a>
            </li>
          )}
          {!locked && isLessThanSmallBreakpoint && (
            <li>Tap the Generate Prompts button</li>
          )}
          <li>Submit a title and essay topic</li>
          <li>Get your essay prompts</li>
        </ol>
      );
    default:
      return null;
  }
};

InitialMessageBullets.propTypes = {
  locked: PropTypes.bool.isRequired,
  quotaRemaining: PropTypes.number.isRequired,
  signupPath: PropTypes.string.isRequired,
  toolName: PropTypes.oneOf(Object.values(AI_TOOL_NAMES)).isRequired,
  upgradeable: PropTypes.bool
};

InitialMessageBullets.defaultProps = {
  upgradeable: false
};

export default InitialMessageBullets;
